import type { ParentingPlanAnswers } from '@/lib/parenting-plan-schemas';
import {
  PREVIEW_DISCLAIMER,
  previewCommunicationSummary,
  previewCustodySummary,
  previewExpenseSummary,
  previewHolidaySummary,
} from '@/lib/parenting-plan-templates';
import { WeekGrid } from './WeekGrid';
import { SCHEDULE_PAGE_META, isScheduleSlug } from '@/lib/parenting-plan-schemas';

type Props = {
  answers: ParentingPlanAnswers;
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className='rounded-lg border border-gray-100 bg-gray-50/80 p-4'>
      <h3 className='mb-2 text-sm font-semibold text-gray-900'>{title}</h3>
      <div className='text-sm text-gray-700 leading-relaxed'>{children}</div>
    </section>
  );
}

export function PlanPreview({ answers }: Props) {
  const slug = answers.custody?.scheduleSlug;
  const meta = slug && isScheduleSlug(slug) ? SCHEDULE_PAGE_META[slug] : null;
  const childrenItems = answers.children?.items?.filter((c) => c.name?.trim()) ?? [];

  return (
    <div className='space-y-4'>
      <div>
        <h2 className='text-lg font-bold text-gray-900'>Live preview</h2>
        <p className='mt-1 text-xs text-gray-500'>{PREVIEW_DISCLAIMER}</p>
      </div>

      {meta ? (
        <WeekGrid pattern={meta.pattern14} parentALabel='Parent A' parentBLabel='Parent B' />
      ) : null}

      <Section title='Children'>
        {childrenItems.length ? (
          <ul className='list-disc pl-4'>
            {childrenItems.map((c, i) => (
              <li key={i}>
                {c.name}
                {c.birthYear ? ` (b. ${c.birthYear})` : ''}
              </li>
            ))}
          </ul>
        ) : (
          <p>Add your children&apos;s first names (and optional birth years).</p>
        )}
      </Section>

      <Section title='Custody'>
        <p>{previewCustodySummary(slug)}</p>
        {answers.custody?.startDate ? (
          <p className='mt-2'>
            <strong>Pattern start:</strong> {answers.custody.startDate}
          </p>
        ) : null}
        {answers.custody?.notes ? <p className='mt-2'>{answers.custody.notes}</p> : null}
      </Section>

      <Section title='Holidays & breaks'>
        {answers.holidays?.notes ? <p>{answers.holidays.notes}</p> : <p>{previewHolidaySummary()}</p>}
      </Section>

      <Section title='Communication'>
        {answers.communication?.channel || answers.communication?.responseTime ? (
          <p>
            {answers.communication.channel ? <>Channel: {answers.communication.channel}. </> : null}
            {answers.communication.responseTime ? (
              <>Expected response time: {answers.communication.responseTime}.</>
            ) : null}
          </p>
        ) : (
          <p>{previewCommunicationSummary()}</p>
        )}
        {answers.communication?.notes ? <p className='mt-2'>{answers.communication.notes}</p> : null}
      </Section>

      <Section title='Expenses'>
        {answers.expenses?.split || answers.expenses?.notes ? (
          <p>
            {answers.expenses.split ? <>Split: {answers.expenses.split}. </> : null}
            {answers.expenses.notes ?? ''}
          </p>
        ) : (
          <p>{previewExpenseSummary()}</p>
        )}
      </Section>

      <Section title='Decisions & disputes'>
        {answers.decisions?.notes ? (
          <p>{answers.decisions.notes}</p>
        ) : (
          <p>Describe major decision categories (education, medical, activities) and how disputes are escalated (e.g. mediation).</p>
        )}
      </Section>
    </div>
  );
}
